import { useMemo, useState } from 'react'
import { IconButton, iconSizes, Modal, Tooltip } from '@common/ui'
import { IconInfoCircle } from '@common/ui/icons'
import classes from './PayloadDocumentation.module.css'

type PayloadDocumentationProps = {
  documentation: string | undefined
}

const renderMarkdown = (md: string): string => {
  const lines = md.split('\n')
  const html: string[] = []
  let inCode = false
  let inTable = false
  let inList = false

  const inline = (text: string) =>
    text
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\[([^\]]+)]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCode) {
        html.push('</code></pre>')
        inCode = false
      } else {
        inCode = true
        html.push('<pre><code>')
      }
      continue
    }
    if (inCode) {
      html.push(line.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '\n')
      continue
    }

    if (/^\s*\|[-| :]+\|\s*$/.test(line)) continue

    if (line.startsWith('|') && line.endsWith('|')) {
      const cells = line
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim())
      if (!inTable) {
        inTable = true
        html.push('<table><thead><tr>' + cells.map((c) => `<th>${inline(c)}</th>`).join('') + '</tr></thead><tbody>')
      } else {
        html.push('<tr>' + cells.map((c) => `<td>${inline(c)}</td>`).join('') + '</tr>')
      }
      continue
    }
    if (inTable) {
      html.push('</tbody></table>')
      inTable = false
    }

    if (/^- /.test(line)) {
      if (!inList) {
        inList = true
        html.push('<ul>')
      }
      html.push(`<li>${inline(line.slice(2))}</li>`)
      continue
    }
    if (inList) {
      html.push('</ul>')
      inList = false
    }

    if (line.startsWith('### ')) {
      html.push(`<h4>${inline(line.slice(4))}</h4>`)
    } else if (line.startsWith('## ')) {
      html.push(`<h3>${inline(line.slice(3))}</h3>`)
    } else if (line.startsWith('# ')) {
      html.push(`<h2>${inline(line.slice(2))}</h2>`)
    } else if (line.trim()) {
      html.push(`<p>${inline(line)}</p>`)
    }
  }

  if (inTable) html.push('</tbody></table>')
  if (inList) html.push('</ul>')
  if (inCode) html.push('</code></pre>')

  return html.join('\n')
}

const PayloadDocumentationButton = ({ documentation }: PayloadDocumentationProps) => {
  const [open, setOpen] = useState(false)
  const html = useMemo(() => (documentation ? renderMarkdown(documentation) : ''), [documentation])

  if (!documentation) return null

  return (
    <>
      <Tooltip label="Payload documentation">
        <IconButton aria-label="Payload documentation" onClick={() => setOpen(true)}>
          <IconInfoCircle size={iconSizes.sm} stroke={1.5} />
        </IconButton>
      </Tooltip>
      <Modal opened={open} onClose={() => setOpen(false)} title="Payload Documentation" size="lg">
        <div className={classes.markdown} dangerouslySetInnerHTML={{ __html: html }} />
      </Modal>
    </>
  )
}

export default PayloadDocumentationButton
